import { authRoutes, settingsRoutes, garageRoutes, routes as appRoutes } from '../routes';

/**
 * Lightweight validation for Next.js + custom routing
 * Next.js already handles basic sanitization, we just need to prevent
 * path traversal and DoS in our custom matching logic
 */
const isValidPathname = (pathname: string) => {
	if (!pathname || typeof pathname !== 'string') {
		return false;
	}

	// Prevent path traversal (even URL-encoded)
	if (pathname.includes('..') || pathname.includes('%2e%2e')) {
		return false;
	}

	// Prevent DoS
	if (pathname.length > 1000) {
		return false;
	}

	return true;
};

interface CompiledPathParam {
	paramName: string;
	isOptional?: boolean;
}
/**
 * Compiles a path into a regular expression and returns the matcher and the params.
 * @param path - The path to compile.
 * @param caseSensitive - Whether the path should be case-sensitive.
 * @param end - Whether the path should match to the end.
 * @returns The matcher and the params.
 */
export function compilePath(
	path: string,
	caseSensitive = false,
	end = true
): [RegExp, CompiledPathParam[]] {
	const params: CompiledPathParam[] = [];
	let regexpSource = `^${path
		.replace(/\/*\*?$/, '') // Ignore trailing / and /*, we'll handle it below
		.replace(/^\/*/, '/') // Make sure it has a leading /
		.replace(/[\\.*+^${}|()[\]]/g, '\\$&') // Escape special regex chars
		.replace(/\/:([\w-]+)(\?)?/g, (_: string, paramName: string, isOptional) => {
			params.push({ paramName, isOptional: isOptional != null });
			return isOptional ? '/?([^\\/]+)?' : '/([^\\/]+)';
		})}`;

	if (path.endsWith('*')) {
		params.push({ paramName: '*' });
		regexpSource +=
			path === '*' || path === '/*'
				? '(.*)$' // Already matched the initial /, just match the rest
				: '(?:\\/(.+)|\\/*)$'; // Don't include the / in params["*"]
	} else if (end) {
		// When matching to the end, ignore trailing slashes
		regexpSource += '\\/*$';
	} else if (path !== '' && path !== '/') {
		// If our path is non-empty and contains anything beyond an initial slash,
		// then we have _some_ form of path in our regex, so we should expect to
		// match only if we find the end of this path segment.  Look for an optional
		// non-captured trailing slash (to match a portion of the URL) or the end
		// of the path (if we've matched to the end).  We used to do this with a
		// word boundary but that gives false positives on routes like
		// /user-preferences since `-` counts as a word boundary.
		regexpSource += '(?:(?=\\/|$))';
	} else {
		// Nothing to match for "" or "/"
	}

	const matcher = new RegExp(regexpSource, caseSensitive ? undefined : 'i');

	return [matcher, params];
}

type Mutable<T> = {
	-readonly [P in keyof T]: T[P];
};

/**
 * Performs pattern matching on a URL pathname and returns information about the match.
 * @param pattern - The pattern to match.
 * @param pathname - The pathname to match.
 * @returns The match information.
 */
export function matchRoute(pattern: string, pathname: string) {
	const [matcher, compiledParams] = compilePath(pattern);

	const match = pathname.match(matcher);
	if (!match) return null;

	const matchedPathname = match[0];
	let pathnameBase = matchedPathname.replace(/(.)\/+$/, '$1');
	const captureGroups = match.slice(1);
	const params: Params = compiledParams.reduce<Mutable<Params>>(
		(memo, { paramName, isOptional }, index) => {
			const memoCopy = { ...memo };
			// We need to compute the pathnameBase here using the raw splat value
			// instead of using params["*"] later because it will be decoded then
			if (paramName === '*') {
				const splatValue = captureGroups[index] || '';
				pathnameBase = matchedPathname
					.slice(0, matchedPathname.length - splatValue.length)
					.replace(/(.)\/+$/, '$1');
			}

			const value = captureGroups[index];
			if (isOptional && !value) {
				memoCopy[paramName] = undefined;
			} else {
				memoCopy[paramName] = (value || '').replace(/%2F/g, '/');
			}
			return memoCopy;
		},
		{}
	);

	return {
		params,
		pathname: matchedPathname,
		pathnameBase,
		pattern
	};
}

/**
 * Determines which route group a pathname belongs to
 * @param pathname - The current pathname to match
 * @returns The route group name or null if no match
 */
export const getRouteGroup = (pathname: string): 'auth' | 'settings' | 'garage' | null => {
	if (!isValidPathname(pathname)) return null;

	const auth = [...authRoutes.loggedIn, ...authRoutes.loggedOut];
	const matchesAuth = auth.some((pattern) => matchRoute(pattern, pathname));
	if (matchesAuth) return 'auth';

	const settings = [...settingsRoutes.loggedIn, ...settingsRoutes.loggedOut];
	const matchesSettings = settings.some((pattern) => matchRoute(pattern, pathname));
	if (matchesSettings) return 'settings';

	const garage = [...garageRoutes.loggedIn];
	const matchesGarage = garage.some((pattern) => matchRoute(pattern, pathname));
	const explicitMatch = ['/garage'].some((pattern) => matchRoute(pattern, pathname));
	if (matchesGarage || explicitMatch) return 'garage';

	return null;
};

/** True for a pathname the middleware sends to sign-in when the session cannot be recovered. */
export const isGuardedRoute = (pathname: string, routeGroup = getRouteGroup(pathname)): boolean => {
	const isMemberRoute = routeGroup === 'garage' || routeGroup === 'settings';
	return isMemberRoute && pathname !== appRoutes.email.verified;
};

export type Params<Key extends string = string> = Readonly<Record<Key, string | undefined>>;

/**
 * Determines if a pathname is a logged-in route for a specific route group
 * @param routeGroup - The route group to check ('auth' | 'settings' | 'garage')
 * @param pathname - The pathname to check
 * @returns True if the pathname is a logged-in route for the given route group
 */
export const isLoggedInRoute = (
	routeGroup: 'auth' | 'settings' | 'garage',
	pathname: string
): boolean => {
	const routeGroupMap = {
		auth: authRoutes.loggedIn,
		settings: settingsRoutes.loggedIn,
		garage: garageRoutes.loggedIn
	} as const;

	const routes = routeGroupMap[routeGroup];
	return routes.some((pattern) => matchRoute(pattern, pathname));
};

/**
 * Determines if a pathname is a logged-out route for a specific route group
 * @param routeGroup - The route group to check ('auth' | 'settings' | 'garage')
 * @param pathname - The pathname to check
 * @returns True if the pathname is a logged-out route for the given route group
 */
export const isLoggedOutRoute = (
	routeGroup: 'auth' | 'settings' | 'garage',
	pathname: string
): boolean => {
	const routeGroupMap = {
		auth: authRoutes.loggedOut,
		settings: settingsRoutes.loggedOut,
		garage: garageRoutes.loggedOut
	} as const;

	const routes = routeGroupMap[routeGroup];
	return routes.some((pattern) => matchRoute(pattern, pathname));
};

// Carried onward, a reset link's token and email reopen its New Password step with a spent token.
const isResetLinkParam = (key: string, params: URLSearchParams): boolean =>
	key === 'token' || (key === 'email' && params.has('token'));

/**
 * Merge the current URL search params, minus a reset link's token and email, into navigateTo
 * @param navigateTo The path to navigate to (can include existing search params)
 * @returns The merged path
 */
export const mergePathWithParams = (navigateTo: string): string => {
	const targetUrl = new URL(navigateTo, window.location.origin);
	const currentParams = new URLSearchParams(window.location.search);
	currentParams.forEach((value, key) => {
		if (!isResetLinkParam(key, currentParams) && !targetUrl.searchParams.has(key)) {
			targetUrl.searchParams.set(key, value);
		}
	});
	return targetUrl.toString().replace(window.location.origin, '');
};

/**
 * Check whether a path (pathname + search) already matches the current browser location
 * @param path The path to compare against the current location
 * @returns True when pathname and search both match
 */
export const isCurrentBrowserLocation = (path: string): boolean => {
	const targetUrl = new URL(path, window.location.origin);
	return (
		targetUrl.pathname === window.location.pathname &&
		targetUrl.search === window.location.search
	);
};
